export const ExperienceDevicePreviewDtoDescriptions = {
  _schema: "Device and path configuration for previewing the experience.",
  device: "Viewport to simulate when rendering the preview.",
  path: "Storefront URL path to load in the preview (e.g., '/products/example').",
} as const;

export const ExperienceCurrencyDtoDescriptions = {
  code: "ISO 4217 currency code used for analytics and price calculations.",
  country: "ISO country code for locale-specific formatting.",
  options: "Additional locale-specific formatting options.",
  symbol: "Display symbol prepended to amounts (e.g., '$', '\u20ac').",
  suffix:
    "Text appended after amounts for postfix currencies (e.g., 'kr' in '100 kr').",
} as const;

export const ExperienceTestTypeDtoDescriptions = {
  hasTestPricing:
    "Server-generated. Read-only. True when price testing is active.",
  hasTestShipping:
    "Server-generated. Read-only. True when shipping rate testing is active.",
  hasTestCampaign:
    "Server-generated. Read-only. True when campaign/offer testing is active.",
  hasTestContent:
    "Server-generated. Read-only. True when any content variation type is enabled.",
  hasTestContentUrl:
    "Server-generated. Read-only. True when URL redirect variations are in use.",
  hasTestContentAdvanced:
    "Server-generated. Read-only. True when custom JS/CSS variations are in use.",
  hasTestContentOnsite:
    "Server-generated. Read-only. True when visual onsite edit variations are in use.",
  hasTestContentTemplate:
    "Server-generated. Read-only. True when Shopify template variations are in use.",
  hasTestContentTheme:
    "Server-generated. Read-only. True when Shopify theme variations are in use.",
  hasTestOnsiteInjections:
    "Server-generated. Read-only. True when CSS/JS injection variations are in use.",
  hasTestCheckoutBlocks:
    "Server-generated. Read-only. True when checkout block variations are in use.",
  hasTestPostPurchase:
    "Server-generated. Read-only. True when post-purchase upsell variations are in use.",
  hasTestOnsiteUpsell:
    "Server-generated. Read-only. True when onsite upsell components are in use.",
} as const;

export const FullExperienceDtoDescriptions = {
  id: "Server-generated. Read-only.",
  name: "Display name shown in dashboards and reports.",
  isPreview:
    "True while in draft mode. Must be set to false before the experience can be started.",
  description: "Internal note explaining what this experience tests.",
  devicePreview: "Device and path configuration for the preview iframe.",
  organizationId:
    "Server-generated. Read-only. The Shopify store that owns this experience.",
  type: "Determines which variation fields are relevant: content/onsiteEdits for DOM edits, content/advanced for CSS/JS injection, content/url for redirect split tests, content/theme for theme tests, content/template for template tests.",
  category:
    "Distinguishes A/B experiments (statistical testing) from personalization rules (deterministic targeting).",
  status:
    "Server-managed lifecycle state. Transitions via start/pause/end actions: pending -> started -> paused/ended.",
  testTypes:
    "Server-generated. Read-only. Flags derived from 'type' indicating which variation capabilities are active.",
  experiencePageTargeting:
    "Rules controlling where this experience activates. Primarily URL/path rules, but the same expression format also supports 'device', 'country', 'visitor', 'trafficSource', 'referrer', and 'cookie'. Empty means all pages. To target the whole experience at a device (e.g. desktop only), prefer an `audience` filter over a page-targeting rule.",
  experienceProductTargeting:
    "Product attribute rules controlling which products this experience applies to. Empty means all products.",
  experienceActions:
    "Server-generated. Read-only. Lifecycle state transitions (start, pause, end, delete, reset) recorded for this experience. On GET /experiences/{experienceId}, each entry includes a `performedBy` field with the triggering user's email, resolved from History records (may be null if unresolvable). Distinct from the list endpoint's raw `history` field, which contains broader user-action records.",
  experienceAnalysis:
    "Controls which orders are included in analytics (test orders only vs sitewide).",
  experienceIntegrations:
    "Connected third-party integrations (e.g., Google Analytics, Segment).",
  experienceCustomMetrics:
    "Custom event-based metrics tracked alongside standard key metrics.",
  experienceKeyMetrics:
    "Business metrics (revenue, conversion, AOV, etc.) used to evaluate test performance. One must be primary.",
  currency:
    "Currency settings for price display and analytics calculations. Null inherits store default.",
  variations:
    "Test variants including the control. One must be control; traffic percentages must sum to 100.",
  createdAtTs: "Server-generated. Read-only. Unix timestamp in milliseconds.",
  isDeleted:
    "Whether this experience has been soft-deleted and hidden from default listings.",
  shippingTestMethodDefinitions:
    "Shipping rate configurations. Only populated for shipping test experiences.",
  shippingCarrierProvider:
    'How shipping rates are sourced for this shipping test. Use "intelligems" for the managed carrier flow and "custom" to use custom provider settings.',
  audience: "Visitor segmentation rules. Null means all visitors are eligible.",
  lastUpdateTs:
    "Server-generated. Read-only. Unix timestamp in milliseconds of the most recent modification.",
  previewPath:
    "Storefront URL path used when generating preview links (e.g., '/products/example').",
  isExternal:
    "True if this experience was created and is managed via the external API rather than the dashboard.",
  startedAtTs:
    "Server-generated. Read-only. Unix timestamp in milliseconds. Null if never started.",
  pausedAtTs:
    "Server-generated. Read-only. Unix timestamp in milliseconds. Null if not currently paused.",
  archivedAtTs:
    "Server-generated. Read-only. Unix timestamp in milliseconds. Null if not archived.",
  requiresLink:
    "When true, visitors must use a special link to enter the experience instead of being auto-assigned.",
  linkBaseUrl:
    "Base URL for generating access links. Only populated when requiresLink is true.",
  endedAtTs:
    "Server-generated. Read-only. Unix timestamp in milliseconds. Null if still active.",
  productVariantCount:
    "Server-generated. Read-only. Total product variants currently included in this experience.",
  sourceExperienceId:
    "Present when this experience was duplicated or rolled out from another. References the original.",
  sourceAction:
    "How this experience was derived from sourceExperienceId. Only present when sourceExperienceId is set.",
  shareToken:
    "Token for generating a public read-only link. Null when sharing is disabled.",
} as const;

export const CreateExperienceDtoDescriptions = {
  id: "Omit to auto-generate.",
  experienceIntegrations:
    "Third-party integrations to connect. Most experiences don't need this at creation time.",
  experienceAnalysis:
    "Controls which orders are included in analytics results.",
  name: "Short display name shown in dashboards and reports. Aim for under ~60 characters. Summarize rather than enumerate — do NOT list every product. For 3 or more products, describe the set (e.g. '+10% price test — 5 bestsellers' or '+$3 price test — Watches collection'), not the individual titles.",
  description:
    "Internal note explaining what this experience tests. Helpful for team context. Free-text and human-owned — it is never regenerated automatically, so if you later add products to this experience, update it yourself if it has gone stale.",
  currency: "Currency for price display and analytics. Omit to default to USD.",
  previewPath:
    "Store URL path for generating preview links (e.g., '/products/example').",
  type: "Determines which variation fields are relevant. Use content/onsiteEdits for DOM changes, content/advanced for CSS/JS injection, content/url for URL redirects, content/theme for theme tests, content/template for template tests.",
  isPreview:
    "Set to false to make immediately eligible for starting. Defaults to true (draft mode).",
} as const;

export const ExternalExperienceDescriptions = {
  id: "Copy from GET response. Identifies which experience to update.",
  type: "Immutable after creation. Must match the existing value from GET response.",
  currency:
    "Currency for price display and analytics. For a PRICE TEST this selects the TEST currency and visitor currency targeting is locked to it, so only shoppers browsing in that currency enter the test. On CREATE, omit it to run in the store's default currency. `code` is a required ISO 4217 code (e.g. 'GBP') whenever the object is sent; `symbol` is derived from the code unless you supply one.",
} as const;

export const ExternalExperienceRequestBodyDescriptions = {
  experience:
    "The complete experience object containing settings, variations, audience, page targeting, and key metrics. For PUT: include id fields from the GET response on all nested entities.",
  variations:
    "Full replacement — include ALL variations to keep. Omitted variations are deleted. One must be control, percentages must sum to 100. Match variation mechanisms to experience type: onsiteEdits for DOM changes, onsiteInjection for CSS/JS, redirects for URL/theme/template tests.",
  audience:
    "Visitor targeting rules. Omit entirely to remove audience targeting from the experience. Each entry in `filters` is { priority, action, filterType, expression }, where `expression` carries the match. IMPORTANT: for an experiment (A/B test, including a price test) a matching filter's action must be an experiment action — use 'randomVariation' (NOT 'experienceInclude', which is campaign/personalization-only and will be rejected). DEVICE TARGETING for a price test — 'desktop only' — is the audience: { enabled: true, audienceType: 'custom', elseAction: 'assignVariation', elseVariationIndex: '0', elseExcludeFromAnalytics: true, filters: [{ priority: 0, action: 'randomVariation', filterType: 'device', expressionType: 'common', expression: [{ query: { key: 'device', value: 'desktop', filter: 'equals', type: 'device' } }] }] }. value is 'any' | 'mobile' | 'desktop'; the else* fields send everyone NOT on that device to the control group (index '0') and out of analytics, which is what makes it 'desktop only'.",
  experiencePageTargeting:
    "Full replacement — include ALL page targeting rules to keep. Omitted rules are deleted. Send AT MOST ONE entry: the editor renders only the first entry that has an expression, so conditions split across several entries are invisible in the UI and are destroyed the next time anyone saves targeting there. Combine every page condition into that single entry's `expression`. That `expression` is in POSTFIX (reverse Polish) order: every condition first, then the operators combining them. An 'and'/'or' operator consumes the TWO results before it — never place an operator between two conditions. To OR three paths A, B and C send [A, B, {operator:'or'}, C, {operator:'or'}]; [A, {operator:'or'}, B, {operator:'or'}, C] is infix, is rejected, and would silently drop conditions. One condition needs no operator; N conditions need exactly N-1, all after the first two. Parentheses are not supported. Each condition is { query: { type: 'urlPath', value: '/products/some-handle', filter: 'contains', key: '' } } — 'urlPath' matches the URL path only and 'key' is unused for it. REJECTED on a split URL test (type 'content/url'): its redirects already scope the test to the pages their originUrl matches, so send an empty array and narrow the redirects' originUrl/filter instead. Other experience types, including ones whose variations carry their own redirects, accept page targeting.",
  experienceKeyMetrics:
    "Full replacement — include ALL metrics to keep. Omitted metrics are deleted. Exactly one must be primary.",
} as const;

export const ExternalExperiencePageTargetingDescriptions = {
  id: "Copy from GET response. Identifies which page targeting rule to preserve.",
} as const;
